import { useState, useEffect } from "react";
import { getEnvironment } from "utils";
import { getCookie } from "utils/cookie";
import appConfig from "config.json";
import {
	loadBrandParam,
	loadLocaleParam,
	validateAndAddStoreParam,
	validateParam,
} from "utils/paramsValidation";
import { getCache, setCache } from "utils/cache";

const { brands, defaultBaseApiUrl } = appConfig;
const userTypeKey = "userType";
const brand = loadBrandParam();
const appEnv = getEnvironment();
const conversationId =
	getCookie("PHPSESSID") ||
	getCookie("Conversation_UUID") ||
	getCookie("convId");

const apiUrl = (() => {
	let serviceEnv = "";
	switch (appEnv) {
		case "development":
		case "latest":
			serviceEnv = "latest.";
			break;
		case "stage":
			serviceEnv = "stage.";
			break;
		case "lt01":
			serviceEnv = brands[brand].useLt01ApiPrefix ? "lt01." : "load.";
			break;
		default:
			serviceEnv = "";
	}
	return (brands[brand]?.baseApiUrl || defaultBaseApiUrl).replace(
		"{env}",
		serviceEnv
	);
})();

const urlInterceptor = (url, locale, options = {}) => {
	if (url?.indexOf("/navigation/") === 0) {
		url = new URL(`${apiUrl}${url.replace("/navigation", "")}`);
		url.searchParams.append("brand", brand);
		url.searchParams.append("locale", locale);
		validateAndAddStoreParam(url);
		if (!options.headers) {
			options.headers = {};
		}
		options.headers["conversation-id"] = conversationId;
		if (brands[brand].usePaCookie && url.pathname.indexOf("/profile")) {
			const paCookie = getCookie("__pa");
			if (paCookie) {
				options.headers["__pa"] = paCookie;
			}
		}
		if (url.searchParams.has("travelAgentHeader")) {
			options.headers["travel-agent-header"] =
				url.searchParams.get("travelAgentHeader");
			url.searchParams.delete("travelAgentHeader");
		}
		if (
			url.pathname.indexOf("/content/") &&
			!url.searchParams.has(userTypeKey)
		) {
			url.searchParams.append(
				userTypeKey,
				url.searchParams.has("store") ? "TRAVEL_AGENT" : "GUEST"
			);
		}
		if (brands[brand].filterContentKeys) {
			url.searchParams.delete("userType");
		}
		if (
			url.pathname.indexOf("/content/") !== -1 &&
			validateParam("customLayout")
		) {
			url.searchParams.append("customLayout", "true");
		}
		url = url.href;
	}
	return { url, options };
};

const useFetch = (url, options, cacheValidMinutes = 0, trigger) => {
	const locale = loadLocaleParam();
	const [response, setResponse] = useState(null);
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(false);
	useEffect(() => {
		const abortController = new AbortController();
		const signal = abortController.signal;
		const doFetch = async () => {
			setError(null);
			setLoading(true);
			try {
				let res;
				const { url: requestUrl, options: optionsWithHeaders } = urlInterceptor(
					url,
					locale,
					options || {}
				);
				if (cacheValidMinutes) {
					res = await getCache(requestUrl);
				}
				if (!res) {
					res = await fetch(requestUrl, {
						...optionsWithHeaders,
						...{ signal },
					});
					if (!res.ok) {
						// eslint-disable-next-line no-throw-literal
						throw {
							body: await res.json(),
							message: res.message || "Request error",
							status: res.status,
							statusText: res.statusText,
							type: res.type,
						};
					}
					if (cacheValidMinutes) {
						await setCache(requestUrl, res.clone(), cacheValidMinutes);
					}
				}
				if (!signal.aborted) {
					if (res.status !== 204) {
						const json = await res.json();
						setResponse(json);
					} else {
						setResponse({});
					}
				}
			} catch (e) {
				console.error(e);
				if (!signal.aborted) {
					setResponse(null);
					setError(e);
				}
			} finally {
				if (!signal.aborted) {
					setLoading(false);
				}
			}
		};
		if (url) {
			doFetch();
		}
		return () => {
			abortController?.abort();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [trigger]);
	return { response, error, loading };
};
export default useFetch;
